import ClearIcon from "@/assets/images/icons/clear-icon.svg";
import MessageSquare from "@/assets/images/icons/message-square.svg";
import SearchIcon from "@/assets/images/icons/search-icon.svg";
import ExternalLink from "@/components/ExternalLink";
import { InlineBrandSpinner } from "@/components/ui/InlineBrandSpinner";
import { DashboardToolbarDropdown } from "@/components/ui/DashboardToolbarDropdown";
import { DashboardOutlinedIconButton } from "@/components/ui/DashboardOutlinedIconButton";
import {
  DESKTOP_SEARCH_MAX_WIDTH,
  SearchInputField,
} from "@/components/ui/SearchInputField";
import { DEFAULT_LESSON_SEARCH_PLACEHOLDER } from "@/lib/courseSearchPlaceholder";
import {
  DASHBOARD_TOOLBAR_CONTROL_HEIGHT,
  DASHBOARD_TOOLBAR_DROPDOWN_WIDTH_LESSONS,
  DASHBOARD_TOOLBAR_ICON_INACTIVE,
} from "@/lib/dashboardToolbarUi";
import { CONTENT_MAX_WIDTH } from "@/lib/useDeviceType";
import { useRouter } from "expo-router";
import React, { useState } from "react";
import { Pressable } from "react-native";
import { Button, SizableText, XStack, YStack, useMedia } from "tamagui";

type CourseSearchBarProps = {
  value: string;
  onChange: (text: string) => void;
  searching?: boolean;
  onClear: () => void;
  courseSlug?: string | null;
  discussionUrl?: string | null;
  rightContent?: React.ReactNode;
  showLessonSearch?: boolean;
  searchPlaceholder?: string;
};

export function CourseSearchBar({
  value,
  onChange,
  searching = false,
  onClear,
  courseSlug,
  discussionUrl,
  rightContent,
  showLessonSearch = true,
  searchPlaceholder = DEFAULT_LESSON_SEARCH_PLACEHOLDER,
}: CourseSearchBarProps) {
  const media = useMedia();
  const isMobile = !media.gtSm;
  const router = useRouter();
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [isLessonsMenuOpen, setIsLessonsMenuOpen] = useState(false);

  const showFullSearch = showLessonSearch && (isSearchActive || !isMobile);
  const showButtons = !showLessonSearch || !isSearchActive || !isMobile;
  const showSearchIconOnly = showLessonSearch && isMobile && !isSearchActive;

  const showSpinner = value.length > 0 && searching;
  const showCloseIcon =
    !showSpinner &&
    ((isMobile && isSearchActive) || (!isMobile && value.length > 0));

  const handleClosePress = () => {
    onClear();
    if (isMobile) setIsSearchActive(false);
  };

  return (
    <YStack pt={32} pb={24} $gtSm={{ pt: 40 }}>
      <YStack w="100%" ai="center">
        <XStack
          w="100%"
          maxWidth={CONTENT_MAX_WIDTH}
          ai="center"
          jc="space-between"
          px={16}
          gap="$3"
          minHeight={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
        >
          <XStack
            ai="center"
            gap="$3"
            h={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
            minHeight={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
            f={isMobile && isSearchActive ? 1 : 0}
            $gtSm={{ f: 1 }}
          >
            {showSearchIconOnly && (
              <DashboardOutlinedIconButton
                onPress={() => setIsSearchActive(true)}
                accessibilityLabel="Search lessons"
              >
                <SearchIcon
                  width={16}
                  height={16}
                  color={DASHBOARD_TOOLBAR_ICON_INACTIVE}
                />
              </DashboardOutlinedIconButton>
            )}

            {showFullSearch && (
              <SearchInputField
                flex={isMobile ? 1 : undefined}
                isMobile={isMobile}
                value={value}
                onChangeText={onChange}
                placeholder={searchPlaceholder}
                onFocus={() => isMobile && setIsSearchActive(true)}
                width={isMobile ? "100%" : undefined}
                containerProps={
                  !isMobile ? { maxWidth: DESKTOP_SEARCH_MAX_WIDTH } : undefined
                }
                trailing={
                  showSpinner ? (
                    <InlineBrandSpinner />
                  ) : showCloseIcon ? (
                    <Pressable
                      onPress={handleClosePress}
                      hitSlop={8}
                      style={{ justifyContent: "center", alignItems: "center" }}
                    >
                      <ClearIcon width={16} />
                    </Pressable>
                  ) : null
                }
              />
            )}

            {showButtons && courseSlug === "jazz-piano-method" && (
              <DashboardToolbarDropdown
                label="All Lessons"
                open={isLessonsMenuOpen}
                onOpenChange={setIsLessonsMenuOpen}
                width={DASHBOARD_TOOLBAR_DROPDOWN_WIDTH_LESSONS}
                content={
                  <YStack gap="$3" p="$3.5">
                    {[
                      {
                        label: "2021 – Present",
                        slug: "all-lessons-since-2021",
                      },
                      { label: "2016 – 2020", slug: "all-lessons-2016-2020" },
                      { label: "2011 – 2015", slug: "all-lessons-until-2015" },
                    ].map((era) => (
                      <Pressable
                        key={era.slug}
                        onPress={() => {
                          setIsLessonsMenuOpen(false);
                          router.replace({
                            pathname:
                              "/courses/[course-slug]/collections/[collection-slug]",
                            params: {
                              "course-slug": String(courseSlug),
                              "collection-slug": era.slug,
                            },
                          });
                        }}
                      >
                        <SizableText
                          color="#5C5854"
                          fontSize="$3"
                          size="$3"
                          fontWeight="600"
                        >
                          {era.label}
                        </SizableText>
                      </Pressable>
                    ))}
                  </YStack>
                }
              />
            )}
          </XStack>

          {showButtons && (rightContent || discussionUrl) && (
            <XStack
              ai="center"
              jc="flex-end"
              gap="$3"
              h={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
              minHeight={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
            >
              {rightContent}
              {discussionUrl && (
                <ExternalLink url={discussionUrl}>
                  {isMobile ? (
                    <Pressable
                      accessibilityRole="button"
                      accessibilityLabel="Course Talk"
                      style={({ pressed }) => ({
                        width: DASHBOARD_TOOLBAR_CONTROL_HEIGHT,
                        height: DASHBOARD_TOOLBAR_CONTROL_HEIGHT,
                        borderRadius: DASHBOARD_TOOLBAR_CONTROL_HEIGHT / 2,
                        backgroundColor: "#0D0D0D",
                        alignItems: "center",
                        justifyContent: "center",
                        opacity: pressed ? 0.85 : 1,
                      })}
                    >
                      <MessageSquare width={16} height={16} />
                    </Pressable>
                  ) : (
                    <Button
                      br={30}
                      bg="#0D0D0D"
                      color="white"
                      icon={<MessageSquare width={16} />}
                      size="$4"
                      $gtMd={{ size: "$3.5" }}
                      fontSize="$3"
                      fontWeight={600}
                      pressStyle={{ opacity: 0.85 }}
                    >
                      Course Talk
                    </Button>
                  )}
                </ExternalLink>
              )}
            </XStack>
          )}
        </XStack>
      </YStack>
    </YStack>
  );
}
